// Packages
import { datadogRum } from '@datadog/browser-rum';

// Types
import type { BookingOrderBody, UpdateBookingOrderBody } from '@white-label-helper/api-parkings-bookings';
import type { Channel, default as Pusher } from 'pusher-js';

// Constants
import { pusherErrorTimeout } from '@white-label-configuration/constants';

// Helpers
import { handlePaymentRequestOriginal, handlePaymentRequestManageBooking, handlePaymentRequestExtendedBooking, handleBookingWithoutPayment, handleAmendBookingWithoutPayment } from '@white-label-helper/api-parkings-bookings';
import getPusher from '@white-label-helper/pusher-channel';
import { addCustomMetric } from '@white-label-helper/shared-helpers';

export type PusherEvent = {
  status: string;
  step: string;
  forward: {
    saga_id: string;
    saga_message_id: string;
  };
  payment_client_secret?: string;
}

let pusher: Pusher;

function handlePaymentError(error: Error, startTime: number, reject?: (reason?: Error) => void | null, timeoutId?: NodeJS.Timeout, context?: object = {}) {
  clearTimeout(timeoutId);

  const errorMessage = error instanceof Error ? JSON.stringify(error.message) : JSON.stringify(error);

  addCustomMetric("error", "Booking Metric: Exception", {
    failure_response: errorMessage,
    event: 'BookingFailed',
    ...context,
  }, startTime, 'booking_service');

  datadogRum.addError(`pusher-handle-payment-request~handlePayment ${errorMessage}`);

  if(reject) {
    reject(error);
  }
}

export function handlePaymentHelper(flowKey: 'original' | 'manageBooking') {
  let eventName: string;

  switch (flowKey) {
    case 'original': {
      eventName = 'OrderCreatingFailedEvent';
      break;
    }
    case 'manageBooking': {
      eventName = 'OrderUpdatingFailedEvent';
      break;
    }
    default: break;
  }

  let channelName: string;
  let timeoutId: NodeJS.Timeout;

  function destroyHandlePayment() {
    if (pusher === undefined) {
      pusher = getPusher();
    }

    pusher.unsubscribe(channelName);
  }

  function handleBookingOrderWithoutPayment(cartToken: string, paymentData: BookingOrderBody | UpdateBookingOrderBody) {
    const startTime = performance.now();
    return new Promise<PusherEvent | void>((resolve, reject) => {
      channelName = `order.${cartToken}`;

      if ('original_order_item_id' in paymentData) {
        handleAmendBookingWithoutPayment({ ...paymentData, send_distribution_api_emails: true })
          .then(() => {
            resolve();
          })
          .catch((error: Error) => {
            handlePaymentError(error, startTime, reject, timeoutId);
          });
      } else {
        handleBookingWithoutPayment({ ...paymentData, send_distribution_api_emails: true })
          .then(() => {
            resolve();
          })
          .catch((error: Error) => {
            handlePaymentError(error, startTime, reject, timeoutId);
          });
      }
    }).catch((error: unknown) => {
      datadogRum.addError(`pusher-handle-payment-request~handleBookingOrderWithoutPayment: ${error}`);
      return Promise.reject(error);
    });
  }

  /**
   * Subscribes to a Pusher channel using the given cart token.
   * Makes sure the subscription is ready before binding to other events,
   * so we don't need to wait for the subscription to succeed and can reduce overall booking time.
   * @param {String} cartToken
   */
  function getPusherChannel(cartToken: string) {
    const startTime = performance.now();

    channelName = `order.${cartToken}`;

    if (pusher === undefined) {
      pusher = getPusher();
    }

    addCustomMetric('tracking', 'pusher-handle-payment-request~getPusherChannel: Pusher subscribed', {
      channelName,
    }, startTime, 'booking_service');

    return pusher.subscribe(channelName);
  }

  function handlePayment(cartToken: string, paymentData: BookingOrderBody | UpdateBookingOrderBody, channel: Channel) {
    const startTime = performance.now();
    return new Promise<PusherEvent>((resolve, reject) => {
      if(!channel) {
        channel = getPusherChannel(cartToken);
      }
      addCustomMetric('tracking', 'pusher-handle-payment-request~handlePayment: Pusher subscribed', {
        channelName,
      }, startTime, 'booking_service');

      channel.bind('OrderPostAuthorizedStatusEvent', (data: PusherEvent) => {
        addCustomMetric('tracking', 'Booking Metric: OrderPostAuthorizedStatusEvent', {
          event: 'OrderPostAuthorizedStatusEvent',
          data,
        }, startTime, 'booking_service');
        clearTimeout(timeoutId);
        resolve(data);
      });

      channel.bind('error', (error: unknown) => {
        clearTimeout(timeoutId);
        reject(error);
      });

      channel.bind(eventName, (error: unknown) => {
        clearTimeout(timeoutId);
        reject({ errorType: eventName, error });
      });

      let handlePaymentRequest;
      if ('upgrading_order_item_id' in paymentData && paymentData.upgrading_order_item_id) {
        handlePaymentRequest = handlePaymentRequestExtendedBooking;
      } else if ('original_order_item_id' in paymentData) {
        handlePaymentRequest = handlePaymentRequestManageBooking;
      } else {
        handlePaymentRequest = handlePaymentRequestOriginal;
      }

      handlePaymentRequest(paymentData).catch((error: Error) => {
        handlePaymentError(error, startTime, reject, timeoutId);
      });

      // Default timeout to reject request if it's frozen
      timeoutId = setTimeout(() => {
        const oneSecond = 1000;
        reject(new Error(`Timed out after ${pusherErrorTimeout / oneSecond} seconds`));
      }, pusherErrorTimeout);
    }).catch((error: unknown) => {
      handlePaymentError(error, startTime, null, timeoutId, {
        channelName,
      });
      return Promise.reject(error);
    });
  }

  return {
    handlePayment,
    destroyHandlePayment,
    handleBookingOrderWithoutPayment,
    getPusherChannel,
  };
}
